import { useEffect, useState } from "react";
import { customReportsApi } from "../../services/customReportsApi";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Spinner } from "../../components/common/Spinner";
import { DataTable } from "../../components/common/DataTable";
import { GapNotice } from "../../components/common/GapNotice";
import { AdminActionButton } from "../../components/common/AdminActionButton";

/** Metric catalog, metric runner, and scheduled-report list/create/delete
 * are wired to the real admin routes (see customReportsApi.js). Report
 * Templates below stay a static, informational list of example multi-metric
 * groupings -- the live catalog runs one allowlisted metric at a time today,
 * so templates don't map onto it 1:1. schedule_cron is saved for a scheduled
 * report but nothing executes it yet (no cron/timer runner wired up
 * server-side) -- saved reports must be run on demand, either from the
 * picker above or the "Run Now" action per saved report below. */
export function CustomReportingPage() {
  const [templates] = useState(customReportsApi.templates());

  const [metrics, setMetrics] = useState(null);
  const [metricsError, setMetricsError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  const [scheduled, setScheduled] = useState(null);
  const [scheduledError, setScheduledError] = useState(null);
  const [form, setForm] = useState({ name: "", metric_key: "", filters: "", schedule_cron: "" });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    customReportsApi
      .listMetrics()
      .then((r) => {
        setMetrics(r.metrics);
        setSelectedMetric((prev) => prev || r.metrics[0]?.metric_key || "");
      })
      .catch((err) => setMetricsError(err.apiMessage || "Failed to load metric catalog"));
    refreshScheduled();
  }, []);

  function refreshScheduled() {
    customReportsApi
      .listScheduledReports()
      .then((r) => setScheduled(r.reports))
      .catch((err) => setScheduledError(err.apiMessage || "Failed to load scheduled reports"));
  }

  async function runMetricKey(metricKey) {
    if (!metricKey) return;
    setSelectedMetric(metricKey);
    setRunning(true);
    setRunError(null);
    try {
      const r = await customReportsApi.runMetric(metricKey);
      setResult(r);
    } catch (err) {
      setRunError(err.apiMessage || "Failed to run metric");
      setResult(null);
    } finally {
      setRunning(false);
    }
  }

  async function submitScheduledReport(e) {
    e.preventDefault();
    setFormError(null);

    let filters;
    if (form.filters.trim()) {
      try {
        filters = JSON.parse(form.filters);
      } catch {
        setFormError("Filters must be valid JSON (or leave blank).");
        return;
      }
    }

    setSaving(true);
    try {
      await customReportsApi.createScheduledReport({
        name: form.name,
        metric_key: form.metric_key,
        ...(filters ? { filters } : {}),
        ...(form.schedule_cron.trim() ? { schedule_cron: form.schedule_cron.trim() } : {}),
      });
      setForm({ name: "", metric_key: "", filters: "", schedule_cron: "" });
      refreshScheduled();
    } catch (err) {
      setFormError(err.apiMessage || "Failed to save scheduled report");
    } finally {
      setSaving(false);
    }
  }

  async function deleteScheduledReport(reportId) {
    await customReportsApi.deleteScheduledReport(reportId);
    refreshScheduled();
  }

  const resultColumns = result?.rows?.length ? Object.keys(result.rows[0]).map((key) => ({ key, header: key })) : [];
  const resultRows = result?.rows?.map((row, i) => ({ ...row, __rowKey: i })) ?? [];

  return (
    <div>
      <h1>Custom Reporting</h1>

      <Card title="Custom Metric Builder" className="section-spacing">
        <p style={{ color: "var(--color-text-muted)" }}>
          Pick a metric from the allowlisted catalog and run it -- no SQL required.
        </p>

        {metricsError && <p style={{ color: "var(--color-danger)" }}>{metricsError}</p>}
        {!metrics && !metricsError && <Spinner />}

        {metrics && (
          <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-end", flexWrap: "wrap", marginBottom: "var(--space-3)" }}>
            <label>
              Metric
              <select
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="touch-target"
                style={{ display: "block" }}
              >
                {metrics.map((m) => (
                  <option key={m.metric_key} value={m.metric_key}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <Button disabled={!selectedMetric || running} onClick={() => runMetricKey(selectedMetric)}>
              {running ? "Running…" : "Run Metric"}
            </Button>
          </div>
        )}

        {runError && <p style={{ color: "var(--color-danger)" }}>{runError}</p>}

        {result && (
          <div className="section-spacing">
            <h3>Results: {result.metric_key}</h3>

            {/*
              * WHAT WAS WITHHELD, SAID BEFORE THE TABLE (#459).
              *
              * Groups smaller than the k-anonymity floor never reach the
              * client. Showing the remaining rows with nothing else said
              * would make a withheld group indistinguishable from an
              * empty one -- so a missing Texas row reads as "no
              * beekeepers in Texas", which is a different claim and a
              * wrong one. That misreading is the whole risk of a floor,
              * and it lands on whoever hands the numbers to a supplier.
              */}
            {result.suppression?.groups_suppressed > 0 && (
              <p style={{
                background: "var(--color-surface-alt, #f6f6f6)",
                borderLeft: "4px solid var(--color-warning)",
                padding: "var(--space-2, 8px) var(--space-3, 12px)",
                margin: "0 0 var(--space-3, 12px)",
              }} role="status">
                <strong>
                  {result.suppression.groups_suppressed} group
                  {result.suppression.groups_suppressed === 1 ? "" : "s"} withheld
                </strong>{" "}
                ({result.suppression.accounts_suppressed} account
                {result.suppression.accounts_suppressed === 1 ? "" : "s"}), because each
                held fewer than {result.suppression.floor}. {result.suppression.note}
              </p>
            )}

            <DataTable
              rows={resultRows}
              rowKey="__rowKey"
              columns={resultColumns}
              /*
               * "No rows returned" is a LIE when everything was
               * suppressed, and it is the most likely state of a regional
               * metric for a long time -- see K_ANONYMITY_FLOOR's note on
               * how few accounts exist today.
               */
              emptyTitle={
                result.suppression?.groups_suppressed > 0
                  ? "Every group was too small to report"
                  : "No rows returned for this metric"
              }
            />
          </div>
        )}
      </Card>

      <Card title="Report Templates" className="section-spacing">
        <p style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
          Example metric groupings for a future multi-metric chart view -- the live catalog above runs one allowlisted
          metric at a time today, so these don't plug directly into it.
        </p>
        {templates.map((t) => (
          <div key={t.id} style={{ padding: "var(--space-2) 0", borderBottom: "1px solid var(--color-border)" }}>
            <strong>{t.name}</strong>
            <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>{t.metrics.join(", ")}</div>
          </div>
        ))}
      </Card>

      <Card title="Scheduled Reports" className="section-spacing">
        <GapNotice>
          schedule_cron is saved with the report but nothing executes it yet -- there's no Timer/cron runner wired up
          server-side, so saved reports are not actually emailed on a schedule today. Use "Run Now" below (or the
          metric picker above) to run one on demand.
        </GapNotice>

        <form
          onSubmit={submitScheduledReport}
          style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "flex-end", margin: "var(--space-3) 0" }}
        >
          <label>
            Name
            <input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="touch-target"
              style={{ display: "block" }}
            />
          </label>
          <label>
            Metric
            <select
              required
              value={form.metric_key}
              onChange={(e) => setForm((f) => ({ ...f, metric_key: e.target.value }))}
              className="touch-target"
              style={{ display: "block" }}
            >
              <option value="">Select…</option>
              {(metrics || []).map((m) => (
                <option key={m.metric_key} value={m.metric_key}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Schedule (cron, optional)
            <input
              value={form.schedule_cron}
              onChange={(e) => setForm((f) => ({ ...f, schedule_cron: e.target.value }))}
              placeholder="0 6 * * *"
              className="touch-target"
              style={{ display: "block" }}
            />
          </label>
          <label>
            Filters (JSON, optional)
            <input
              value={form.filters}
              onChange={(e) => setForm((f) => ({ ...f, filters: e.target.value }))}
              placeholder='{"tier":"AI"}'
              className="touch-target"
              style={{ display: "block" }}
            />
          </label>
          <Button type="submit" disabled={saving}>
            {saving ? "Saving…" : "Save Report"}
          </Button>
        </form>
        {formError && <p style={{ color: "var(--color-danger)" }}>{formError}</p>}

        {scheduledError && <p style={{ color: "var(--color-danger)" }}>{scheduledError}</p>}
        {!scheduled && !scheduledError && <Spinner />}

        {scheduled && (
          <DataTable
            rows={scheduled}
            rowKey="report_id"
            emptyTitle="No scheduled reports saved yet"
            columns={[
              { key: "name", header: "Name" },
              { key: "metric_key", header: "Metric" },
              { key: "schedule_cron", header: "Schedule", render: (r) => r.schedule_cron || "—" },
              {
                key: "last_run_date",
                header: "Last Run",
                render: (r) => (r.last_run_date ? new Date(r.last_run_date).toLocaleString() : "Never (on-demand only)"),
              },
              { key: "is_active", header: "Active", render: (r) => (r.is_active ? "Yes" : "No") },
              {
                key: "actions",
                header: "Actions",
                render: (r) => (
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    <Button size="sm" variant="secondary" onClick={() => runMetricKey(r.metric_key)}>
                      Run Now
                    </Button>
                    <AdminActionButton
                      label="Delete"
                      confirmTitle={`Delete "${r.name}"?`}
                      confirmBody={<p>This removes the saved report definition. It does not affect any data it reports on.</p>}
                      onConfirm={() => deleteScheduledReport(r.report_id)}
                    />
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Card title="Data Integrity Checks (Automated Daily)">
        <GapNotice>
          Needs a Timer-triggered function running a fixed set of consistency queries (orphaned foreign keys,
          negative inventory counts, RLS predicate sanity checks) and a place to persist results over time so this
          page can show trends rather than just "last run." Nothing runs these checks today.
        </GapNotice>
      </Card>
    </div>
  );
}
